/**
 * Générateur — Calculer et interpréter la moyenne simple d'une série de données.
 * Attendu ID : 976 | Niveau : 5ème | Domaine : Statistiques (descripteur 131)
 *
 * Version conforme à la spécification 976 (2026-09-21).
 * Ajoutées : « Les relevés de températures » (série d'entiers RELATIFS de 15 à
 * 30 valeurs — la troisième nature de valeurs de la spec, et le niveau 3 de la
 * graduation, qu'aucune carte ne servait) et « Au-dessus ou en dessous ? »
 * (les deux questions d'interprétation du niveau 4 : combien de valeurs
 * dépassent la moyenne, et dans quel sens la moyenne se déplace si l'on ajoute
 * une valeur — sans recalculer).
 * Recalibrées : toutes les séries tiennent désormais dans les bornes de la
 * spec (effectif 5 à 30, valeurs de -20 à 120, une décimale au plus). Les
 * séries de 2 et 3 valeurs du snowboard, les tailles en mètres à deux
 * décimales du volley et les temps au centième du 400 mètres en sortaient.
 *
 * Toute l'arithmétique est ENTIÈRE (dixièmes, centièmes, secondes) ; les
 * divisibilités sont garanties PAR CONSTRUCTION, jamais par arrondi caché.
 *
 * Plan de cartes :
 *   « Calculer une moyenne » (niveau 1) — 5 à 8 entiers de 12 à 120, dont UNE
 *     valeur répétée, quotient exact : la moyenne, puis le QCM dont les trois
 *     distracteurs sont les trois croyances de la spec.
 *   « Les SMS de Mélanie » (niveau 1) — 5 entiers, quotient non entier mais
 *     EXACT au dixième : aucun arrondi n'est demandé ni accepté.
 *   « Le score de snowboard » (niveau 1) — 5 descentes en minutes et secondes :
 *     la conversion est imposée en étapes, somme divisible par 5.
 *   « L'équipe de volley » (niveau 2) — 12 joueurs dont un à 0 point (il compte
 *     dans l'effectif), quotient non exact, arrondi au dixième annoncé ; QCM de
 *     formule de tableur dont le distracteur divise par 11.
 *   « Qui a gagné ? » (niveau 2) — deux séries de 12 parties, arrondi au
 *     dixième annoncé, écart des totaux garanti : la comparaison des totaux
 *     suffit puisque l'effectif est le même.
 *   « Les prospectus publicitaires » (niveau 2) — 12 valeurs lues sur un
 *     diagramme en barres, arrondi au dixième annoncé.
 *   « Le 400 mètres » (niveau 3) — 15 à 18 temps décimaux à UNE décimale,
 *     somme puis moyenne arrondie au centième.
 *   « Les relevés de températures » (niveau 3) — 15 à 30 entiers RELATIFS de
 *     -20 à 15, quotient exact : le point dur est la somme signée.
 *   « Les tours de piste » (niveau 4) — extrêmes uniques : moyenne des 10
 *     séances, puis des 8 séances restantes, puis interprétation de l'écart.
 *   « Le javelot » (niveau 4) — 8 décimaux : prévoir la moyenne quand toutes
 *     les données augmentent de 1 m, ou sont doublées, sans tout recalculer.
 *   « Au-dessus ou en dessous ? » (niveau 4) — combien de valeurs dépassent la
 *     moyenne, et dans quel sens la moyenne se déplace si l'on ajoute une
 *     valeur strictement au-dessus ou au-dessous.
 *
 * Spécification appliquée :
 *   Bornes : effectif de la série 5..30 ; valeurs de -20 à 120, entières ou à
 *     UNE décimale ; arrondi de la moyenne au dixième (niveaux 1 et 2) ou au
 *     centième (niveau 3), et SEULEMENT quand le quotient n'est pas exact ;
 *     écart minimal de 5 entre la plus petite et la plus grande valeur ; au
 *     moins une valeur au-dessus et une au-dessous de la moyenne ; valeur
 *     ajoutée au niveau 4 strictement au-dessus ou au-dessous de la moyenne.
 *   Exclusions : aucune moyenne pondérée, par aucun chemin (aucun tableau
 *     valeur/effectif, aucune somme de produits, le diagramme des prospectus
 *     porte douze VALEURS et non des effectifs) ; aucune médiane, étendue ni
 *     quartile ; aucune série constante ; aucun effectif au-delà de trente ;
 *     aucun mélange de natures dans une même série.
 *   Erreurs visées → mots-clés (`_meta.params.distracteurs[].code`) :
 *     - diviser par le nombre de valeurs distinctes → `valeurs_distinctes_pour_diviseur`
 *     - additionner des relatifs en ignorant les signes → `signes_ignores_dans_la_somme`
 *     - diviseur emprunté à la question précédente → `diviseur_emprunte`
 *     - croire que la moyenne est une valeur de la série → `moyenne_valeur_de_la_serie`
 *     - croire que la moyenne est au milieu des extrêmes → `moyenne_au_milieu_des_extremes`
 *     - présenter un quotient arrondi comme exact → `arrondi_presente_comme_exact`
 *   Un code SUPPLÉMENTAIRE, signalé comme tel : `linearite_ignoree` (carte du
 *     javelot). La spec n'associe aucune erreur visée à la linéarité de la
 *     moyenne, que la carte travaille pourtant.
 *   Graduation → cartes : 1 « Calculer une moyenne », « Les SMS de Mélanie »,
 *     « Le score de snowboard » ; 2 « L'équipe de volley », « Qui a gagné ? »,
 *     « Les prospectus publicitaires » ; 3 « Le 400 mètres », « Les relevés de
 *     températures » ; 4 « Les tours de piste », « Le javelot », « Au-dessus ou
 *     en dessous ? ».
 *   Non tranché : la spec demande, pour l'interprétation, un choix unique PUIS
 *     un champ libre de justification non corrigé ; aucun format TanoEd ne
 *     juxtapose les deux, et l'interprétation est servie en QCM dont chaque
 *     choix porte sa justification.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou,
} from '../../../utils';
import { imgElement, svgBarres, ligneTable, tableEnonce } from './svgStats';

const ATTENDU_ID = 976;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_BRUT = 'Calculer une moyenne';
const L_SMS = 'Les SMS de Mélanie';
const L_SNOW = 'Le score de snowboard';
const L_VOLLEY = 'L\'équipe de volley';
const L_GAGNE = 'Qui a gagné ?';
const L_PROSPECTUS = 'Les prospectus publicitaires';
const L_COURSE = 'Le 400 mètres';
const L_TEMPERATURE = 'Les relevés de températures';
const L_TOURS = 'Les tours de piste';
const L_JAVELOT = 'Le javelot';
const L_INTERP = 'Au-dessus ou en dessous ?';

// ─── Helpers (écritures FR depuis des entiers) ───────────────

/** Nombre décimal → écriture française (virgule, pas de zéro final). */
const fmtNb = (x) => String(x).replace('.', ',');

/** dixièmes entiers → « 64,4 » / « 64 » (le signe est conservé). */
const fmtDixiemes = (d) => {
    const s = d < 0 ? '-' : '';
    const a = Math.abs(d);
    return a % 10 === 0 ? `${s}${a / 10}` : `${s}${Math.trunc(a / 10)},${a % 10}`;
};

/** centièmes entiers → « 45,1 » (sans zéro final) / « 45 » si entier. */
const fmtCentiemes = (c) => {
    const e = Math.trunc(c / 100);
    const r = c % 100;
    if (r === 0) return String(e);
    return `${e},${String(r).padStart(2, '0').replace(/0$/, '')}`;
};

/** Tire n valeurs dans [lo;hi] dont la somme est ≡ 0 (mod n) : la dernière est
 *  ajustée dans sa plage (par construction, jamais d'échec). */
const tirerDivisible = (n, lo, hi) => {
    const vals = Array.from({ length: n - 1 }, () => randInt(lo, hi));
    const partielle = vals.reduce((a, b) => a + b, 0);
    let dernier = randInt(lo, hi);
    // Modulo NORMALISÉ : le reste de JS est négatif quand la somme l'est, et
    // l'ajustement sortirait alors de la plage par le haut (séries de relatifs).
    dernier -= (((partielle + dernier) % n) + n) % n;
    if (dernier < lo) dernier += n;
    vals.push(dernier);
    return shuffle(vals);
};

const somme = (t) => t.reduce((a, b) => a + b, 0);

/** Garantit qu'un tableau vrai/faux n'est ni tout vrai ni tout faux, sans boucle. */
const equilibrer = (vs) => {
    const nbVrai = vs.filter(Boolean).length;
    if (nbVrai === 0) vs[0] = true;
    else if (nbVrai === vs.length) vs[0] = false;
    return vs;
};

/** Contrôles communs à toute série (spec : écart minimal, jamais constante,
 *  au moins une valeur au-dessus ET une au-dessous de la moyenne). */
const serieValide = (vals, moyenne) => {
    if (Math.max(...vals) - Math.min(...vals) < 5) return false;
    if (!vals.some((v) => v > moyenne) || !vals.some((v) => v < moyenne)) return false;
    return true;
};

/** Bloc `_meta.params` commun (la moyenne pondérée est exclue structurellement). */
const paramsSerie = (vals, moyenne, nature, niveau, extra = {}) => ({
    niveau,
    effectif_de_la_serie: vals.length,
    valeur_de_la_serie: vals,
    nature_des_valeurs: nature,
    moyenne_attendue: moyenne,
    ecart_entre_la_valeur_minimale_et_la_valeur_maximale: Math.max(...vals) - Math.min(...vals),
    nombre_de_valeurs_superieures_a_la_moyenne: vals.filter((v) => v > moyenne).length,
    nombre_de_valeurs_inferieures_a_la_moyenne: vals.filter((v) => v < moyenne).length,
    serie_ponderee: false,
    ...extra,
});

// ═════════════════════════════════════════════════════════════
// CARTE « Calculer une moyenne » (niveau 1 — quotient exact)
// ═════════════════════════════════════════════════════════════

/** 5 à 8 entiers de 12 à 120, dont UNE valeur répétée (le diviseur « nombre de
 *  valeurs distinctes » devient une erreur possible), quotient exact, moyenne
 *  absente de la série et différente du milieu des extrêmes. */
const tirerBrut = () => {
    for (let essai = 0; essai < 300; essai++) {
        const n = randInt(5, 8);
        const base = [];
        while (base.length < n - 1) {
            const v = randInt(12, 120);
            if (!base.includes(v)) base.push(v);
        }
        const vals = shuffle([...base, randomChoice(base)]);
        const s = somme(vals);
        if (s % n !== 0) continue;
        const m = s / n;
        if (vals.includes(m)) continue;
        if (!serieValide(vals, m)) continue;
        const milieu = (Math.min(...vals) + Math.max(...vals)) / 2;
        if (milieu === m) continue;
        const proche = vals.reduce((a, b) => (Math.abs(b - m) < Math.abs(a - m) ? b : a));
        const parDistinctes = Math.round(s / (n - 1));
        if (new Set([m, milieu, proche, parDistinctes]).size !== 4) continue;
        return { n, vals, s, m, milieu, proche, parDistinctes };
    }
    const vals = [24, 24, 61, 78, 45, 68];
    return { n: 6, vals, s: 300, m: 50, milieu: 51, proche: 45, parDistinctes: 60 };
};

const genBrutSaisie = () => {
    const d = tirerBrut();
    const payload = makeSaisie(String(d.m),
        `<p>Calcule la moyenne de la série de ${d.n} données : ${d.vals.join(' ; ')}.</p>`
        + '<p>Le quotient est exact : aucun arrondi n\'est demandé.</p>',
        { ...opts(L_BRUT), strict: false });
    payload._meta.params = paramsSerie(d.vals, d.m, 'entiers naturels', 1, { quotient_exact: true });
    return payload;
};

const genBrutQcm = () => {
    const d = tirerBrut();
    const bonne = String(d.m);
    const dist = [
        { code: 'valeurs_distinctes_pour_diviseur', valeur: String(d.parDistinctes) },
        { code: 'moyenne_au_milieu_des_extremes', valeur: fmtNb(d.milieu) },
        { code: 'moyenne_valeur_de_la_serie', valeur: String(d.proche) },
    ];
    const payload = makeQcm([bonne, ...dist.map((x) => x.valeur)], bonne,
        `<p>Voici une série de ${d.n} données : ${d.vals.join(' ; ')}.</p>`
        + '<p>Quelle est la moyenne de cette série ?</p>',
        opts(L_BRUT));
    payload._meta.params = paramsSerie(d.vals, d.m, 'entiers naturels', 1, {
        nombre_de_valeurs_distinctes: d.n - 1,
        distracteurs: dist,
    });
    return payload;
};

// ═════════════════════════════════════════════════════════════
// CARTE « Les SMS de Mélanie » (niveau 1 — quotient exact au dixième)
// ═════════════════════════════════════════════════════════════

/** 5 entiers de 15 à 48, somme NON divisible par 5 : la moyenne n'est pas
 *  entière, mais elle est EXACTE au dixième — aucun arrondi n'est demandé. */
const tirerSms = () => {
    for (let essai = 0; essai < 120; essai++) {
        const vals = Array.from({ length: 5 }, () => randInt(15, 48));
        const s = somme(vals);
        if (s % 5 === 0) continue;
        const moyDix = (s * 10) / 5;
        if (!serieValide(vals, moyDix / 10)) continue;
        return { vals, s, moyDix };
    }
    return { vals: [42, 27, 35, 41, 21], s: 166, moyDix: 332 };
};

const introSms = (d) => `<p>Voici le nombre de SMS envoyés par Mélanie sur 5 jours : ${d.vals.join(' ; ')}.</p>`;

const genSmsTrous = () => {
    const d = tirerSms();
    if (randInt(0, 1) === 0) {
        const phrase = `Total des 5 jours : {{${d.s}}} SMS\nNombre moyen de SMS par jour : {{${fmtDixiemes(d.moyDix)}}}`;
        const payload = makeTexteTrou(phrase,
            `${introSms(d)}<p>Calcule le nombre total de SMS, puis le nombre moyen de SMS envoyés par jour. `
            + 'Le quotient est exact : donne-le sans l\'arrondir (avec une virgule si besoin).</p>',
            { ...opts(L_SMS), mode: 'saisie', strict: false });
        payload._meta.params = paramsSerie(d.vals, d.moyDix / 10, 'entiers naturels', 1, { quotient_exact: true, nombre_de_trous: 2 });
        return payload;
    }
    const ecarts = d.vals.map((v) => Math.abs(v * 10 - d.moyDix));
    const phrase = d.vals.map((v, i) => `Jour ${i + 1}, ${v} SMS : {{${fmtDixiemes(ecarts[i])}}}`).join('\n');
    const payload = makeTexteTrou(phrase,
        `${introSms(d)}<p>La moyenne vaut ${fmtDixiemes(d.moyDix)} SMS par jour. Complète l'écart à la moyenne `
        + 'de chaque jour (une distance : toujours positive, avec une virgule si besoin).</p>',
        { ...opts(L_SMS), mode: 'saisie', strict: false });
    payload._meta.params = paramsSerie(d.vals, d.moyDix / 10, 'entiers naturels', 1, { quotient_exact: true, nombre_de_trous: 5 });
    return payload;
};

const genSmsQcm = () => {
    const d = tirerSms();
    const bonne = 'Oui : une moyenne peut être un nombre non entier, même quand toutes les données sont entières';
    const dist = [
        { code: 'moyenne_valeur_de_la_serie', valeur: 'Non : une moyenne est toujours l\'une des valeurs de la série' },
        { code: 'moyenne_valeur_de_la_serie', valeur: 'Non : la moyenne de nombres entiers est forcément un nombre entier' },
        { code: 'arrondi_presente_comme_exact', valeur: `Non : il faut rendre ${Math.round(d.moyDix / 10)} SMS par jour, un nombre entier` },
    ];
    const payload = makeQcm([bonne, ...dist.map((x) => x.valeur)], bonne,
        `${introSms(d)}<p>Mélanie calcule sa moyenne et trouve ${fmtDixiemes(d.moyDix)} SMS par jour, un nombre non entier, `
        + 'alors qu\'elle n\'a jamais envoyé de morceau de SMS. Ce résultat est-il possible ?</p>',
        opts(L_SMS));
    payload._meta.params = paramsSerie(d.vals, d.moyDix / 10, 'entiers naturels', 1, { distracteurs: dist });
    return payload;
};

// ═════════════════════════════════════════════════════════════
// CARTE « Le score de snowboard » (niveau 1 — conversion imposée)
// ═════════════════════════════════════════════════════════════

/** 5 descentes de 60 à 115 s (1 min à 1 min 55 s), somme divisible par 5. */
const tirerSnow = () => {
    for (let essai = 0; essai < 120; essai++) {
        const temps = tirerDivisible(5, 60, 115);
        const moy = somme(temps) / 5;
        if (!serieValide(temps, moy)) continue;
        return { temps, moy };
    }
    return { temps: [75, 92, 68, 110, 100], moy: 89 };
};

const enMinS = (s) => `${Math.trunc(s / 60)} min ${s % 60} s`;

const genSnowTrous = () => {
    const d = tirerSnow();
    const phrase = d.temps.map((t, i) => `Descente ${i + 1} : {{${t}}} s`).join('\n')
        + `\nTemps moyen en secondes : {{${d.moy}}} s`
        + `\nTemps moyen en minutes et secondes : {{${Math.trunc(d.moy / 60)}}} min {{${d.moy % 60}}} s`;
    const texte = `<p>Tom réalise cinq descentes de slalom : ${d.temps.map(enMinS).join(' ; ')}.</p>`
        + '<p>Convertis d\'abord chaque temps en secondes (1 min vaut 60 s), puis calcule le temps moyen '
        + 'des cinq descentes et exprime-le en minutes et secondes.</p>';
    const payload = makeTexteTrou(phrase, texte, { ...opts(L_SNOW), mode: 'saisie', strict: false });
    payload._meta.params = paramsSerie(d.temps, d.moy, 'entiers naturels', 1, { quotient_exact: true, nombre_de_trous: 8 });
    return payload;
};

const genSnowSaisie = () => {
    const scores = tirerDivisible(5, 40, 95);
    const moy = somme(scores) / 5;
    const payload = makeSaisie(String(moy),
        `<p>En half-pipe, Tom enchaîne cinq passages et obtient les scores suivants, en points : ${scores.join(' ; ')}.</p>`
        + '<p>Calcule sa moyenne de points sur les cinq passages. Le quotient est exact.</p>',
        { ...opts(L_SNOW), strict: false, suffixe: 'points' });
    payload._meta.params = paramsSerie(scores, moy, 'entiers naturels', 1, { quotient_exact: true });
    return payload;
};

// ═════════════════════════════════════════════════════════════
// CARTE « L'équipe de volley » (niveau 2 — arrondi au dixième)
// ═════════════════════════════════════════════════════════════

const PRENOMS_VOLLEY = ['Léo', 'Emma', 'Noah', 'Jade', 'Liam', 'Chloé',
    'Milo', 'Anna', 'Thomas', 'Sarah', 'Hugo', 'Lina'];

/** 12 joueurs dont UN à 0 point (il compte dans l'effectif) ; somme non
 *  congrue à 0 modulo 6, donc la moyenne n'est pas exacte au dixième. */
const tirerVolley = () => {
    for (let essai = 0; essai < 300; essai++) {
        const iZero = randInt(0, 11);
        const points = Array.from({ length: 12 }, (_, i) => (i === iZero ? 0 : randInt(8, 40)));
        const s = somme(points);
        if (s % 6 === 0) continue;                       // quotient exact au dixième : écarté
        const moyDix = Math.round((s * 10) / 12);
        if (!serieValide(points, moyDix / 10)) continue;
        return { points, iZero, s, moyDix };
    }
    const points = [18, 27, 0, 36, 22, 25, 30, 24, 34, 19, 26, 20];
    return { points, iZero: 2, s: 281, moyDix: Math.round(2810 / 12) };
};

const tableVolley = (d) => tableEnonce(
    ligneTable('Joueur', PRENOMS_VOLLEY),
    ligneTable('Points marqués', d.points),
);

const INTRO_VOLLEY = '<p>Voici les points marqués par les 12 joueurs d\'une équipe de volley au cours '
    + 'de la saison. Un joueur n\'a marqué aucun point, mais il fait bien partie de l\'équipe.</p>';

const genVolleySaisie = () => {
    const d = tirerVolley();
    const payload = makeSaisie(fmtDixiemes(d.moyDix),
        `${INTRO_VOLLEY}${tableVolley(d)}`
        + `<p>Calcule le nombre moyen de points marqués par joueur, ARRONDI AU DIXIÈME. Attention : `
        + `${PRENOMS_VOLLEY[d.iZero]}, qui n'a marqué aucun point, compte dans l'effectif de l'équipe.</p>`,
        { ...opts(L_VOLLEY), strict: false, suffixe: 'points' });
    payload._meta.params = paramsSerie(d.points, d.moyDix / 10, 'entiers naturels', 2, {
        arrondi_de_la_moyenne: 1,
        quotient_exact: false,
        erreurVisee: 'arrondi_presente_comme_exact',
    });
    return payload;
};

const genVolleyQcm = () => {
    const d = tirerVolley();
    const bonne = '=MOYENNE(B2:B13)';
    const dist = [
        { code: 'diviseur_emprunte', valeur: '=MOYENNE(B2:B12)' },
        { code: 'diviseur_emprunte', valeur: '=SOMME(B2:B13)/11' },
        { code: 'valeurs_distinctes_pour_diviseur', valeur: '=SOMME(B2:B13)' },
    ];
    const payload = makeQcm([bonne, ...dist.map((x) => x.valeur)], bonne,
        `${INTRO_VOLLEY}${tableVolley(d)}`
        + '<p>On saisit ce tableau dans un tableur : les points occupent les cellules B2 à B13, '
        + 'une par joueur. Quelle formule faut-il saisir en B14 pour calculer le nombre moyen de points '
        + `des 12 joueurs ? Rappel : les 12 joueurs comptent, y compris ${PRENOMS_VOLLEY[d.iZero]}.</p>`,
        opts(L_VOLLEY));
    payload._meta.params = paramsSerie(d.points, d.moyDix / 10, 'entiers naturels', 2, { distracteurs: dist });
    return payload;
};

// ═════════════════════════════════════════════════════════════
// CARTE « Qui a gagné ? » (niveau 2 — même effectif, comparer les totaux)
// ═════════════════════════════════════════════════════════════

/** Deux séries de 12 parties, totaux écartés d'au moins 3 (aucune ambiguïté
 *  d'arrondi), quotients non exacts au dixième. */
const tirerGagne = () => {
    for (let essai = 0; essai < 300; essai++) {
        const alice = Array.from({ length: 12 }, () => randInt(10, 34));
        const seb = Array.from({ length: 12 }, () => randInt(10, 34));
        const sa = somme(alice);
        const sb = somme(seb);
        if (Math.abs(sa - sb) < 3) continue;
        if (sa % 6 === 0 || sb % 6 === 0) continue;
        const ma = Math.round((sa * 10) / 12);
        const ms = Math.round((sb * 10) / 12);
        if (!serieValide(alice, ma / 10) || !serieValide(seb, ms / 10)) continue;
        return { alice, seb, sa, sb, ma, ms };
    }
    const alice = [12, 24, 22, 16, 34, 23, 18, 27, 15, 31, 20, 29];
    const seb = [14, 17, 23, 15, 32, 26, 12, 21, 19, 28, 16, 22];
    return { alice, seb, sa: 271, sb: 245, ma: Math.round(2710 / 12), ms: Math.round(2450 / 12) };
};

const introGagne = (d) =>
    `<p>Alice et Sébastien comparent leurs scores sur 12 parties. Alice : ${d.alice.join(' ; ')}. `
    + `Sébastien : ${d.seb.join(' ; ')}.</p>`;

const genGagneTrous = () => {
    const d = tirerGagne();
    const phrase = `Moyenne d'Alice : {{${fmtDixiemes(d.ma)}}}\nMoyenne de Sébastien : {{${fmtDixiemes(d.ms)}}}`;
    const payload = makeTexteTrou(phrase,
        `${introGagne(d)}<p>Calcule les deux moyennes, ARRONDIES AU DIXIÈME (avec une virgule).</p>`,
        { ...opts(L_GAGNE), mode: 'saisie', strict: false });
    payload._meta.params = paramsSerie(d.alice, d.ma / 10, 'entiers naturels', 2, {
        arrondi_de_la_moyenne: 1,
        quotient_exact: false,
        erreurVisee: 'arrondi_presente_comme_exact',
        nombre_de_trous: 2,
    });
    return payload;
};

const genGagneQcm = () => {
    const d = tirerGagne();
    const gagnant = d.sa > d.sb ? 'd\'Alice' : 'de Sébastien';
    const bonne = `Comparer les totaux suffit : même nombre de parties, donc le plus grand total (${Math.max(d.sa, d.sb)}, celui ${gagnant}) donne la plus grande moyenne`;
    const dist = [
        { code: 'arrondi_presente_comme_exact', valeur: 'Il faut obligatoirement calculer les deux moyennes au centième près avant de conclure' },
        { code: 'moyenne_valeur_de_la_serie', valeur: 'Il suffit de comparer le meilleur score de chacun' },
        { code: 'moyenne_au_milieu_des_extremes', valeur: 'Il suffit de comparer le milieu entre le plus petit et le plus grand score de chacun' },
    ];
    const payload = makeQcm([bonne, ...dist.map((x) => x.valeur)], bonne,
        `${introGagne(d)}<p>Alice et Sébastien ont joué le même nombre de parties, 12. `
        + 'Comment savoir qui a la meilleure moyenne sans poser de division ?</p>',
        opts(L_GAGNE));
    payload._meta.params = paramsSerie(d.alice, d.ma / 10, 'entiers naturels', 2, { distracteurs: dist });
    return payload;
};

// ═════════════════════════════════════════════════════════════
// CARTE « Les prospectus publicitaires » (niveau 2 — lecture puis moyenne)
// ═════════════════════════════════════════════════════════════

const MOIS_COURTS = ['janv.', 'févr.', 'mars', 'avril', 'mai', 'juin',
    'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];

/** 12 valeurs multiples de 5 dans [20;70], somme non congrue à 0 modulo 6 :
 *  la moyenne n'est pas exacte au dixième. */
const tirerProspectus = () => {
    for (let essai = 0; essai < 300; essai++) {
        const vals = Array.from({ length: 12 }, () => 5 * randInt(4, 14));
        const s = somme(vals);
        if (s % 6 === 0) continue;
        const moyDix = Math.round((s * 10) / 12);
        if (!serieValide(vals, moyDix / 10)) continue;
        return { vals, s, moyDix };
    }
    const vals = [40, 45, 50, 40, 35, 45, 50, 55, 45, 40, 45, 55];
    return { vals, s: 545, moyDix: Math.round(5450 / 12) };
};

const genProspectusSaisie = () => {
    const d = tirerProspectus();
    const payload = makeSaisie(fmtDixiemes(d.moyDix),
        '<p>Le diagramme donne le nombre de prospectus publicitaires reçus par un habitant de Lille '
        + 'chaque mois de l\'année 2025. Chaque mois porte une seule valeur.</p>'
        + '<p>Lis les douze valeurs sur le diagramme, puis calcule le nombre moyen de prospectus reçus '
        + 'par mois, ARRONDI AU DIXIÈME.</p>',
        { ...opts(L_PROSPECTUS), strict: false, suffixe: 'prospectus' });
    payload.elements_additionnels = imgElement(svgBarres(MOIS_COURTS, d.vals,
        { yStep: 5, yLabel: 'Prospectus', xLabel: 'Mois (2025)' }));
    payload._meta.params = paramsSerie(d.vals, d.moyDix / 10, 'entiers naturels', 2, {
        arrondi_de_la_moyenne: 1,
        quotient_exact: false,
        erreurVisee: 'arrondi_presente_comme_exact',
    });
    return payload;
};

// ═════════════════════════════════════════════════════════════
// CARTE « Le 400 mètres » (niveau 3 — décimaux, arrondi au centième)
// ═════════════════════════════════════════════════════════════

/** 15 à 18 temps à UNE décimale (42,0 à 46,9 s) ; la somme des dixièmes n'est
 *  pas divisible par l'effectif, donc la moyenne n'est pas exacte au centième. */
const tirerCourse = () => {
    for (let essai = 0; essai < 300; essai++) {
        const n = randInt(15, 18);
        const ds = Array.from({ length: n }, () => randInt(415, 485));
        const sDs = somme(ds);
        if ((sDs * 10) % n === 0) continue;
        const moyCs = Math.round((sDs * 10) / n);
        if (!serieValide(ds.map((x) => x / 10), moyCs / 100)) continue;
        return { n, ds, sDs, moyCs };
    }
    const ds = [431, 447, 425, 462, 438, 478, 418, 469, 443, 436, 452, 428, 460, 434, 449];
    const sDs = somme(ds);
    return { n: 15, ds, sDs, moyCs: Math.round((sDs * 10) / 15) };
};

const genCourseTrous = () => {
    const d = tirerCourse();
    const phrase = `Somme des ${d.n} temps : {{${fmtDixiemes(d.sDs)}}} s`
        + `\nTemps moyen arrondi au centième : {{${fmtCentiemes(d.moyCs)}}} s`;
    const texte = `<p>Voici les temps des ${d.n} concurrents des séries d'un 400 mètres, en secondes : `
        + `${d.ds.map(fmtDixiemes).join(' ; ')}.</p>`
        + `<p>Calcule la somme des ${d.n} temps, puis le temps moyen ARRONDI AU CENTIÈME de seconde. `
        + 'Le quotient n\'est pas exact : l\'arrondi est demandé.</p>';
    const payload = makeTexteTrou(phrase, texte, { ...opts(L_COURSE), mode: 'saisie', strict: false });
    payload._meta.params = paramsSerie(d.ds.map((x) => x / 10), d.moyCs / 100, 'décimaux à une décimale', 3, {
        arrondi_de_la_moyenne: 2,
        quotient_exact: false,
        erreurVisee: 'arrondi_presente_comme_exact',
        nombre_de_trous: 2,
    });
    return payload;
};

// ═════════════════════════════════════════════════════════════
// CARTE « Les relevés de températures » (niveau 3 — entiers relatifs)
// ═════════════════════════════════════════════════════════════

/** 15 à 30 relevés entiers de -20 à 15, somme divisible par l'effectif
 *  (quotient exact, aucun arrondi demandé), au moins un relevé négatif. */
const tirerTemperature = () => {
    for (let essai = 0; essai < 300; essai++) {
        const n = randInt(15, 30);
        const vals = tirerDivisible(n, -20, 15);
        if (!vals.some((v) => v < 0)) continue;
        const m = somme(vals) / n;
        if (!serieValide(vals, m)) continue;
        const sommeAbsolue = somme(vals.map(Math.abs));
        if (sommeAbsolue % n !== 0) {
            return { n, vals, m, sansSignes: Math.round(sommeAbsolue / n) };
        }
        if (sommeAbsolue / n === m) continue;
        return { n, vals, m, sansSignes: sommeAbsolue / n };
    }
    const vals = [-8, 3, -5, 7, -12, 0, 4, -3, 9, -6, 2, -1, 5, -9, 14];
    return { n: 15, vals, m: 0, sansSignes: 6 };
};

const introTemperature = (d) => `<p>Une station météo a relevé la température, en degrés Celsius, `
    + `pendant ${d.n} jours de l'hiver : ${d.vals.join(' ; ')}.</p>`;

const genTemperatureSaisie = () => {
    const d = tirerTemperature();
    const payload = makeSaisie(String(d.m),
        `${introTemperature(d)}<p>Calcule la température moyenne de ces ${d.n} jours. Attention aux signes : `
        + 'le quotient est exact, aucun arrondi n\'est demandé.</p>',
        { ...opts(L_TEMPERATURE), strict: false, suffixe: '°C' });
    payload._meta.params = paramsSerie(d.vals, d.m, 'entiers relatifs', 3, {
        quotient_exact: true,
        erreurVisee: 'signes_ignores_dans_la_somme',
    });
    return payload;
};

const genTemperatureQcm = () => {
    const d = tirerTemperature();
    const milieu = (Math.min(...d.vals) + Math.max(...d.vals)) / 2;
    const proche = d.vals.reduce((a, b) => (Math.abs(b - d.m) < Math.abs(a - d.m) ? b : a));
    const bonne = String(d.m);
    const brut = [
        { code: 'signes_ignores_dans_la_somme', valeur: String(d.sansSignes) },
        { code: 'moyenne_au_milieu_des_extremes', valeur: fmtNb(milieu) },
        { code: 'moyenne_valeur_de_la_serie', valeur: String(proche) },
    ];
    // Un distracteur qui coïnciderait avec la bonne réponse ou avec un autre
    // distracteur est écarté, puis remplacé par un écart d'une unité codé de
    // la même façon : le nombre de propositions reste CONSTANT (quatre).
    const vus = new Set([bonne]);
    const dist = [];
    brut.forEach((x) => {
        let v = x.valeur;
        let k = 1;
        while (vus.has(v)) { v = fmtNb(Number(String(x.valeur).replace(',', '.')) + k); k += 1; }
        vus.add(v);
        dist.push({ code: x.code, valeur: v });
    });
    const payload = makeQcm([bonne, ...dist.map((x) => x.valeur)], bonne,
        `${introTemperature(d)}<p>Quelle est la température moyenne de ces ${d.n} jours ?</p>`,
        opts(L_TEMPERATURE));
    payload._meta.params = paramsSerie(d.vals, d.m, 'entiers relatifs', 3, { distracteurs: dist });
    return payload;
};

// ═════════════════════════════════════════════════════════════
// CARTE « Les tours de piste » (niveau 4 — effet des valeurs extrêmes)
// ═════════════════════════════════════════════════════════════

/** 10 valeurs : minimum et maximum UNIQUES et détachés du reste ; somme
 *  divisible par 10 et somme sans les extrêmes divisible par 8. */
const tirerTours = () => {
    for (let essai = 0; essai < 200; essai++) {
        const min = randInt(12, 16);
        const centre = tirerDivisible(8, 25, 45);
        const sc = somme(centre);
        let max = randInt(68, 78);
        max -= (sc + min + max) % 10;
        if (max <= 60 || max > 90) continue;
        const vals = shuffle([min, ...centre, max]);
        const m10 = (sc + min + max) / 10;
        if (!serieValide(vals, m10)) continue;
        return { vals, min, max, sc, m10, m8: sc / 8 };
    }
    const centre = [30, 44, 28, 41, 33, 39, 26, 43];
    const vals = shuffle([14, ...centre, 72]);
    return { vals, min: 14, max: 72, sc: 284, m10: 37, m8: 35.5 };
};

const genToursTrous = () => {
    const d = tirerTours();
    const phrase = `Moyenne des 10 séances : {{${fmtNb(d.m10)}}} tours`
        + `\nValeur la plus petite : {{${d.min}}} tours`
        + `\nValeur la plus grande : {{${d.max}}} tours`
        + `\nMoyenne des 8 séances restantes : {{${fmtNb(d.m8)}}} tours`;
    const texte = `<p>Un athlète note son nombre de tours de piste à chaque entraînement : ${d.vals.join(' ; ')}.</p>`
        + '<p>Calcule la moyenne des 10 séances, relève la plus petite puis la plus grande valeur, '
        + 'puis calcule la moyenne des 8 séances restantes.</p>';
    const payload = makeTexteTrou(phrase, texte, { ...opts(L_TOURS), mode: 'saisie', strict: false });
    payload._meta.params = paramsSerie(d.vals, d.m10, 'entiers naturels', 4, {
        quotient_exact: true,
        moyenne_sans_les_extremes: d.m8,
        nombre_de_trous: 4,
    });
    return payload;
};

const genToursQcm = () => {
    const d = tirerTours();
    const bonne = 'La moyenne est sensible aux valeurs extrêmes : deux séances exceptionnelles suffisent à la déplacer';
    const dist = [
        { code: 'moyenne_au_milieu_des_extremes', valeur: 'Les deux moyennes devraient être égales, puisque la moyenne est toujours au milieu entre la plus petite et la plus grande valeur' },
        { code: 'moyenne_au_milieu_des_extremes', valeur: 'Retirer la plus petite et la plus grande valeur ne change jamais une moyenne : elles se compensent' },
        { code: 'moyenne_valeur_de_la_serie', valeur: 'L\'une des deux moyennes est fausse, car une moyenne est toujours l\'une des valeurs de la série' },
    ];
    const payload = makeQcm([bonne, ...dist.map((x) => x.valeur)], bonne,
        `<p>Un athlète note ses tours de piste : ${d.vals.join(' ; ')}.</p>`
        + `<p>La moyenne des 10 séances vaut ${fmtNb(d.m10)} tours ; sans les deux séances extrêmes `
        + `(${d.min} et ${d.max}), elle vaut ${fmtNb(d.m8)} tours. Comment expliquer cette différence ?</p>`,
        opts(L_TOURS));
    payload._meta.params = paramsSerie(d.vals, d.m10, 'entiers naturels', 4, {
        moyenne_sans_les_extremes: d.m8,
        distracteurs: dist,
    });
    return payload;
};

// ═════════════════════════════════════════════════════════════
// CARTE « Le javelot » (niveau 4 — prévoir sans recalculer)
// ═════════════════════════════════════════════════════════════

/** 8 performances en dixièmes de mètre (55,0 à 72,0 m), somme divisible par 8. */
const tirerJavelot = () => {
    for (let essai = 0; essai < 120; essai++) {
        const dm = tirerDivisible(8, 550, 720);
        const moyDm = somme(dm) / 8;
        if (!serieValide(dm.map((x) => x / 10), moyDm / 10)) continue;
        return { dm, moyDm };
    }
    const dm = [552, 610, 688, 574, 700, 636, 592, 664];
    return { dm, moyDm: somme(dm) / 8 };
};

const genJavelotTrous = () => {
    const d = tirerJavelot();
    const phrase = `Moyenne des 8 lancers : {{${fmtDixiemes(d.moyDm)}}} m`
        + `\nMoyenne si chaque athlète gagne 1 mètre : {{${fmtDixiemes(d.moyDm + 10)}}} m`
        + `\nMoyenne si chaque performance est doublée : {{${fmtDixiemes(d.moyDm * 2)}}} m`;
    const texte = `<p>Les performances de huit athlètes au lancer de javelot, en mètres : ${d.dm.map(fmtDixiemes).join(' ; ')}.</p>`
        + '<p>Calcule la longueur moyenne des lancers. Puis, sans tout recalculer : quelle serait la moyenne si '
        + 'chaque athlète augmentait sa performance de 1 mètre, et si chaque performance était doublée ?</p>';
    const payload = makeTexteTrou(phrase, texte, { ...opts(L_JAVELOT), mode: 'saisie', strict: false });
    payload._meta.params = paramsSerie(d.dm.map((x) => x / 10), d.moyDm / 10, 'décimaux à une décimale', 4, {
        quotient_exact: true,
        nombre_de_trous: 3,
    });
    return payload;
};

const genJavelotQcm = () => {
    const d = tirerJavelot();
    const bonne = 'Non : si toutes les données augmentent de 1, la moyenne augmente de 1';
    const dist = [
        { code: 'linearite_ignoree', valeur: 'Oui : une moyenne doit toujours être recalculée en entier' },
        { code: 'linearite_ignoree', valeur: 'Non : la moyenne ne change pas quand toutes les données changent' },
        { code: 'linearite_ignoree', valeur: 'Non : la moyenne augmente de 8, une fois par athlète' },
    ];
    const payload = makeQcm([bonne, ...dist.map((x) => x.valeur)], bonne,
        `<p>Les performances de huit athlètes au javelot ont pour moyenne ${fmtDixiemes(d.moyDm)} m. `
        + 'Chaque athlète augmente sa performance de 1 mètre. Faut-il refaire tout le calcul '
        + 'pour connaître la nouvelle moyenne ?</p>',
        opts(L_JAVELOT));
    payload._meta.params = paramsSerie(d.dm.map((x) => x / 10), d.moyDm / 10, 'décimaux à une décimale', 4, { distracteurs: dist });
    return payload;
};

// ═════════════════════════════════════════════════════════════
// CARTE « Au-dessus ou en dessous ? » (niveau 4 — les deux interprétations)
// ═════════════════════════════════════════════════════════════

/** 10 entiers de 5 à 40, somme NON divisible par 10 : la moyenne a un dixième
 *  non nul, donc aucune valeur de la série ne lui est égale et la question
 *  « combien de valeurs la dépassent » a une réponse unique. */
const tirerInterp = () => {
    for (let essai = 0; essai < 200; essai++) {
        const vals = Array.from({ length: 10 }, () => randInt(5, 40));
        const s = somme(vals);
        if (s % 10 === 0) continue;
        const moyDix = s;                                  // moyenne en dixièmes : somme / 10
        const m = moyDix / 10;
        if (!serieValide(vals, m)) continue;
        const au_dessus = vals.filter((v) => v > m).length;
        if (au_dessus === 5) continue;                     // le cas qui valide la croyance des deux moitiés
        const hausse = randInt(0, 1) === 0;
        const ajout = hausse
            ? Math.min(120, Math.ceil(m) + randInt(5, 20))
            : Math.max(0, Math.floor(m) - randInt(5, 20));
        if (Math.abs(ajout - m) < 3) continue;
        return { vals, s, m, moyDix, au_dessus, ajout, hausse };
    }
    const vals = [12, 35, 8, 27, 19, 31, 6, 24, 15, 37];
    return { vals, s: 214, m: 21.4, moyDix: 214, au_dessus: 4, ajout: 38, hausse: true };
};

const introInterp = (d) => `<p>Voici les 10 scores d'un joueur à un jeu de société : ${d.vals.join(' ; ')}. `
    + `Leur moyenne vaut ${fmtDixiemes(d.moyDix)}.</p>`;

const genInterpSaisie = () => {
    const d = tirerInterp();
    const payload = makeSaisie(String(d.au_dessus),
        `${introInterp(d)}<p>Combien de ces scores sont strictement au-dessus de la moyenne ?</p>`,
        { ...opts(L_INTERP), strict: false, suffixe: 'scores' });
    payload._meta.params = paramsSerie(d.vals, d.m, 'entiers naturels', 4, {
        erreurVisee: 'moyenne_au_milieu_des_extremes',
    });
    return payload;
};

const genInterpQcm = () => {
    const d = tirerInterp();
    const bonne = d.hausse
        ? `Elle augmente : ${d.ajout} est plus grand que la moyenne ${fmtDixiemes(d.moyDix)}`
        : `Elle diminue : ${d.ajout} est plus petit que la moyenne ${fmtDixiemes(d.moyDix)}`;
    const dist = [
        { code: 'moyenne_au_milieu_des_extremes', valeur: `Elle ne change pas : la moyenne reste au milieu entre ${Math.min(...d.vals)} et ${Math.max(...d.vals)}` },
        { code: 'moyenne_valeur_de_la_serie', valeur: `Elle devient égale à ${d.ajout}, puisque la moyenne est une valeur de la série` },
        { code: 'moyenne_au_milieu_des_extremes', valeur: d.hausse
            ? 'Elle diminue : la moyenne se rapproche toujours du milieu de la série'
            : 'Elle augmente : la moyenne se rapproche toujours du milieu de la série' },
    ];
    const payload = makeQcm([bonne, ...dist.map((x) => x.valeur)], bonne,
        `${introInterp(d)}<p>On ajoute un onzième score de ${d.ajout} à la série. Sans refaire le calcul, `
        + 'que devient la moyenne ?</p>',
        opts(L_INTERP));
    payload._meta.params = paramsSerie(d.vals, d.m, 'entiers naturels', 4, {
        valeur_ajoutee_pour_la_question_d_interpretation: d.ajout,
        distracteurs: dist,
    });
    return payload;
};

const genInterpVF = () => {
    const d = tirerInterp();
    const vs = equilibrer([0, 1, 2, 3].map(() => randInt(0, 1) === 0));
    const codes = [];
    if (!vs[2]) codes.push('moyenne_au_milieu_des_extremes');
    if (!vs[3]) codes.push('moyenne_valeur_de_la_serie');
    const sensVrai = d.hausse ? 'augmente' : 'diminue';
    const sensFaux = d.hausse ? 'diminue' : 'augmente';
    const lignes = [
        [`${vs[0] ? d.au_dessus : d.vals.length - d.au_dessus} scores de la série sont strictement au-dessus de la moyenne.`, vs[0] ? 0 : 1],
        [`Si on ajoute un score de ${d.ajout} à la série, la moyenne ${vs[1] ? sensVrai : sensFaux}.`, vs[1] ? 0 : 1],
        [vs[2]
            ? 'La moyenne n\'est pas toujours au milieu entre la plus petite et la plus grande valeur de la série.'
            : 'La moyenne est toujours au milieu entre la plus petite et la plus grande valeur de la série.', vs[2] ? 0 : 1],
        [vs[3]
            ? 'La moyenne n\'est pas forcément l\'une des valeurs de la série.'
            : 'La moyenne est forcément l\'une des valeurs de la série.', vs[3] ? 0 : 1],
    ];
    const melange = shuffle(lignes);
    const payload = makeVraiFaux(melange.map((x) => x[0]), melange.map((x) => x[1]),
        `${introInterp(d)}<p>Vrai ou faux ?</p>`, opts(L_INTERP));
    payload._meta.params = paramsSerie(d.vals, d.m, 'entiers naturels', 4, {
        valeur_ajoutee_pour_la_question_d_interpretation: d.ajout,
        distracteurs: codes.map((code) => ({ code })),
    });
    return payload;
};

// ─── Registry & Export ───────────────────────────────────────

const GENERATORS = {
    brut_texte: genBrutSaisie,
    brut_qcm_unique: genBrutQcm,
    sms_texte_trou_saisie: genSmsTrous,
    sms_qcm_unique: genSmsQcm,
    snow_texte_trou_saisie: genSnowTrous,
    snow_texte: genSnowSaisie,
    volley_texte: genVolleySaisie,
    volley_qcm_unique: genVolleyQcm,
    gagne_texte_trou_saisie: genGagneTrous,
    gagne_qcm_unique: genGagneQcm,
    prospectus_texte: genProspectusSaisie,
    course_texte_trou_saisie: genCourseTrous,
    temperature_texte: genTemperatureSaisie,
    temperature_qcm_unique: genTemperatureQcm,
    tours_texte_trou_saisie: genToursTrous,
    tours_qcm_unique: genToursQcm,
    javelot_texte_trou_saisie: genJavelotTrous,
    javelot_qcm_unique: genJavelotQcm,
    interp_texte: genInterpSaisie,
    interp_qcm_unique: genInterpQcm,
    interp_tableau_vf: genInterpVF,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    relier: 'Relier', ordonner: 'Ordonner', classer: 'Classer', tableau_vf: 'Vrai/Faux',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'brut', label: L_BRUT, formatsList: buildFormats('brut') },
    { id: 'sms', label: L_SMS, formatsList: buildFormats('sms') },
    { id: 'snow', label: L_SNOW, formatsList: buildFormats('snow') },
    { id: 'volley', label: L_VOLLEY, formatsList: buildFormats('volley') },
    { id: 'gagne', label: L_GAGNE, formatsList: buildFormats('gagne') },
    { id: 'prospectus', label: L_PROSPECTUS, formatsList: buildFormats('prospectus') },
    { id: 'course', label: L_COURSE, formatsList: buildFormats('course') },
    { id: 'temperature', label: L_TEMPERATURE, formatsList: buildFormats('temperature') },
    { id: 'tours', label: L_TOURS, formatsList: buildFormats('tours') },
    { id: 'javelot', label: L_JAVELOT, formatsList: buildFormats('javelot') },
    { id: 'interp', label: L_INTERP, formatsList: buildFormats('interp') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
